import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { talentAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, MapPin, Calendar, Clock, DollarSign,
  CheckCircle2, Clock3, XCircle, Award, Ban,
} from 'lucide-react';
import { formatTimeRange, parseLocalDate, formatDate, formatFullDateTime12h } from '../../utils/timeFormat';
import { toast } from 'sonner';
import WithdrawApplicationButton from './WithdrawApplicationButton';

// Display-status → presentation. Source of truth is the backend's
// derived `display_status` (see /api/talent/applications).
const STATUS_PRESENTATION = {
  applied:      { label: 'Applied · Pending review', className: 'bg-sky-100 text-sky-800 border-sky-200',           Icon: Clock3 },
  selected:     { label: 'Selected',                 className: 'bg-emerald-100 text-emerald-800 border-emerald-200', Icon: CheckCircle2 },
  booked:       { label: 'Booked',                   className: 'bg-cyan-100 text-cyan-800 border-cyan-200',          Icon: CheckCircle2 },
  not_selected: { label: 'Not selected',             className: 'bg-gray-100 text-gray-700 border-gray-200',          Icon: XCircle },
  withdrawn:    { label: 'Withdrawn',                className: 'bg-gray-100 text-gray-700 border-gray-200',          Icon: Ban },
  cancelled:    { label: 'Cancelled',                className: 'bg-amber-100 text-amber-800 border-amber-200',       Icon: Ban },
  completed:    { label: 'Completed',                className: 'bg-violet-100 text-violet-800 border-violet-200',    Icon: Award },
};

const filters = [
  { key: 'all',         label: 'All' },
  { key: 'applied',     label: 'Pending' },
  { key: 'selected',    label: 'Selected' },
  { key: 'booked',      label: 'Booked' },
  { key: 'completed',   label: 'Completed' },
  { key: 'not_selected', label: 'Not selected' },
  { key: 'withdrawn',   label: 'Withdrawn' },
  { key: 'cancelled',   label: 'Cancelled' },
];

const StatusChip = ({ status }) => {
  const p = STATUS_PRESENTATION[status] || STATUS_PRESENTATION.applied;
  const Icon = p.Icon;
  return (
    <Badge className={`${p.className} text-xs`} data-testid={`applied-gigs-status-${status}`}>
      <Icon className="w-3 h-3 mr-1" /> {p.label}
    </Badge>
  );
};

const MyApplications = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await talentAPI.getApplications();
      setApplications(response.data || []);
    } catch (error) {
      toast.error('Failed to load applied gigs');
    } finally {
      setLoading(false);
    }
  };

  const counts = useMemo(() => {
    const c = { all: applications.length };
    applications.forEach((a) => {
      const k = a.display_status || 'applied';
      c[k] = (c[k] || 0) + 1;
    });
    return c;
  }, [applications]);

  const visible = useMemo(() => (
    activeFilter === 'all'
      ? applications
      : applications.filter((a) => (a.display_status || 'applied') === activeFilter)
  ), [activeFilter, applications]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="applied-gigs-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate('/talent/dashboard')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-3 mb-1">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="applied-gigs-heading">Applied Gigs</h1>
            <p className="text-sm text-gray-500 mt-1">
              Track every gig you've applied to. Applying does not mean booked — admin reviews each application before confirming.
            </p>
          </div>
          <Button
            onClick={() => navigate('/talent/shifts')}
            className="bg-cyan-500 hover:bg-cyan-600 self-start sm:self-end"
            data-testid="browse-more-shifts-btn"
          >
            Browse more gigs
          </Button>
        </div>

        {/* Status filter strip */}
        <div className="flex flex-wrap gap-2 mt-5 mb-5" data-testid="applied-gigs-filters">
          {filters.map((f) => {
            const active = activeFilter === f.key;
            const n = counts[f.key] || 0;
            return (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className={`text-xs sm:text-sm px-3 py-1.5 rounded-full border transition-colors ${
                  active
                    ? 'bg-cyan-500 text-white border-cyan-500'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
                data-testid={`filter-${f.key}`}
              >
                {f.label}
                <span className={`ml-1.5 text-[10px] ${active ? 'opacity-90' : 'text-gray-400'}`}>({n})</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
          </div>
        ) : visible.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground mb-4">
                {activeFilter === 'all' ? "You haven't applied to any gigs yet." : 'Nothing in this status.'}
              </p>
              <Button onClick={() => navigate('/talent/shifts')} className="bg-cyan-500 hover:bg-cyan-600">
                Browse Available Gigs
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3" data-testid="applied-gigs-list">
            {visible.map((app) => (
              <Card key={app.id} data-testid="applied-gig-card" className="hover:shadow-md transition-shadow cursor-pointer" onClick={(e) => { if (e.target.closest('button,a,[role="button"]')) return; if (app.shift_id) navigate(`/talent/gigs/${app.shift_id}`); }}>
                <CardContent className="p-5">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{app.role_name}</h3>
                        <StatusChip status={app.display_status || 'applied'} />
                      </div>

                      <div className="space-y-1.5 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 shrink-0" />
                          <span className="truncate">{app.venue_name || 'Venue'} • {app.city}, {app.state}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 shrink-0" />
                          <span>{formatDate(app.gig_date)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 shrink-0" />
                          <span>{formatTimeRange(app.start_time, app.end_time)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 shrink-0" />
                          <span>${app.hourly_rate}/hr</span>
                        </div>
                      </div>

                      <div className="mt-3 text-xs text-gray-400">
                        Applied {formatFullDateTime12h(app.applied_at)}
                      </div>
                    </div>
                    <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      {['applied', 'selected'].includes(app.display_status || 'applied') && (
                        <WithdrawApplicationButton
                          applicationId={app.id}
                          onWithdrawn={() => fetchApplications()}
                        />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyApplications;
